/**
 * Financial API client via BOB backend (JWT) — no Odoo portal password.
 * Method names match OdooStatementClient so existing pages keep working.
 */

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_BASE_DELAY_MS = 500;
const DEFAULT_MAX_DELAY_MS = 8000;
const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function backoffDelayMs(attempt, baseDelayMs, maxDelayMs) {
  const exponential = baseDelayMs * 2 ** attempt;
  const jitter = Math.random() * baseDelayMs;
  return Math.min(exponential + jitter, maxDelayMs);
}

export class BobFinancialError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = "BobFinancialError";
    this.status = status;
    this.payload = payload;
  }
}

function detailMessage(payload, status) {
  const detail = payload?.detail ?? payload?.error;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail.message === "string") return detail.message;
  return `HTTP ${status}`;
}

export class BobFinancialClient {
  constructor(token, baseUrl = "", retryOptions = {}) {
    this.token = token;
    // Prefer same-origin so Cloudflare Pages Functions can intercept routes
    // (e.g. sales-order PDF) that are not yet on Render.
    const raw =
      baseUrl ||
      (typeof window !== "undefined" ? "" : process.env.REACT_APP_BACKEND_URL) ||
      "";
    this.baseUrl = String(raw).replace(/\/$/, "");
    this.retryOptions = retryOptions;
  }

  async get(path) {
    const {
      maxRetries = DEFAULT_MAX_RETRIES,
      baseDelayMs = DEFAULT_BASE_DELAY_MS,
      maxDelayMs = DEFAULT_MAX_DELAY_MS,
    } = this.retryOptions;

    let lastError;
    for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
      try {
        return await this.fetchJson(path);
      } catch (error) {
        lastError = error;
        const retryable =
          error instanceof TypeError ||
          (error instanceof BobFinancialError && RETRYABLE_STATUS.has(error.status));
        if (attempt >= maxRetries || !retryable) throw error;
        await sleep(backoffDelayMs(attempt, baseDelayMs, maxDelayMs));
      }
    }
    throw lastError;
  }

  async fetchJson(path) {
    const response = await fetch(`${this.baseUrl}/api/financial${path}`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${this.token}`,
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new BobFinancialError(detailMessage(payload, response.status), response.status, payload);
    }
    return payload;
  }

  getSummary() {
    return this.get("/statement/summary");
  }

  getDashboardSummary() {
    return this.get("/dashboard/summary");
  }

  getLines(params = {}) {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set("date_from", params.dateFrom);
    if (params.dateTo) query.set("date_to", params.dateTo);
    if (params.page) query.set("page", String(params.page));
    if (params.limit) query.set("limit", String(params.limit));
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return this.get(`/statement/lines${suffix}`);
  }

  getInvoices(params = {}) {
    return this.getList("/invoices", params);
  }

  getPayments(params = {}) {
    return this.getList("/payments", params);
  }

  getSalesOrders(params = {}) {
    return this.getList("/sales-orders", params);
  }

  getSalesOrder(orderId) {
    return this.get(`/sales-orders/${orderId}`);
  }

  async getSalesOrderPdfBlob(orderId) {
    const response = await fetch(`${this.baseUrl}/api/financial/sales-orders/${orderId}/pdf`, {
      headers: {
        Accept: "application/pdf",
        Authorization: `Bearer ${this.token}`,
      },
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new BobFinancialError(detailMessage(payload, response.status), response.status, payload);
    }
    return response.blob();
  }

  async getPartnerLedgerPdfBlob(params = {}) {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set("date_from", params.dateFrom);
    if (params.dateTo) query.set("date_to", params.dateTo);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    const response = await fetch(`${this.baseUrl}/api/financial/statement/pdf${suffix}`, {
      headers: {
        Accept: "application/pdf",
        Authorization: `Bearer ${this.token}`,
      },
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new BobFinancialError(detailMessage(payload, response.status), response.status, payload);
    }
    return response.blob();
  }

  getList(path, params = {}) {
    const query = new URLSearchParams();
    if (params.dateFrom) query.set("date_from", params.dateFrom);
    if (params.dateTo) query.set("date_to", params.dateTo);
    if (params.page) query.set("page", String(params.page));
    if (params.offset != null) query.set("offset", String(params.offset));
    if (params.limit) query.set("limit", String(params.limit));
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return this.get(`${path}${suffix}`);
  }
}
